<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Equipment for spray applications
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
                <i class="actions__item zmdi zmdi-plus" v-b-tooltip="'Add equipment'" @click="enableCreatingEquipment"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>

        <div class="card">
            <div class="card-header bg-primary text-white">
                Equipment
            </div>
            <b-table small hover stacked="md" :items="filteredData" :fields="table.fields" sort-by="name" :show-empty="true">

                <template slot="calibrated_on" slot-scope="row">
                    <template v-if="row.item.calibrated_on">
                        {{ row.item.calibrated_on | date_format }}
                    </template>
                    <span v-else class="text-muted">(unknown)</span>
                </template>
                <template slot="actions" slot-scope="row">

                    <b-button variant="secondary btn--icon"
                              v-b-tooltip.hover="'Edit equipment'"
                              @click.prevent="enableEditingEquipment(row.item)">
                        <i class="zmdi zmdi-edit"></i>
                    </b-button>

                    <a :href="row.item.image" :download="buildFileName(row.item)" class="btn btn-orange btn--icon"
                       v-b-tooltip="'Download Image'" target="_blank"
                       v-if="row.item.image">
                        <i class="zmdi zmdi-download"></i>
                    </a>
                    <button class="btn btn-orange btn--icon" disabled v-else>
                        <i class="zmdi zmdi-download"></i>
                    </button>
                </template>
            </b-table>
        </div>

        <b-modal id="editModal" size="md" ref="modal" v-model="editingEnabled" :lazy="true"
                 :title="`${editingEquipment ? 'Edit' : 'New'} equipment - Type: Spray Unit`"
                 :ok-disabled="buttonsDisabled" :cancel-disabled="buttonsDisabled"
                 @hidden="disableEditingEquipment" @ok="handleEquipmentSave">

            <div class="form-group">
                <label>Name</label>
                <div class="position-relative">
                    <input type="text" name="name" data-vv-as="name" v-validate="{required: true}" class="form-control" v-model="name">
                    <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                    <i class="form-group__bar"></i>
                </div>
            </div>

            <div class="form-group">
                <label>Calibration Date</label>
                <div class="position-relative">
                    <VueDatePicker
                            id="date"
                            v-model="calibrated_on"
                            :config="datePicker"
                            class="form-control"
                            :class="{'is-invalid': errors.has('calibrated_on')}"
                            placeholder="Select date"
                            name="calibrated_on" />
                    <i class="form-group__bar"></i>
                    <div class="invalid-feedback" v-if="errors.has('calibrated_on')">{{ errors.first('calibrated_on') }}</div>
                </div>
            </div>

            <div class="form-group">
                <label>Equipment Image
                    <template v-if="editingEquipment"> (leave empty if you do not wish to change)</template>
                </label>
                <div class="position-relative">
                    <b-form-file v-model="equipmentImage" @input="uploadNewEquipmentImage" accept="image/*" placeholder="Choose an image..." />
                    <b-progress :value="equipmentImageProgress" animated v-if="equipmentImageProgress"
                                :variant="equipmentImageProgressVariant" />
                </div>
            </div>

            <template slot="modal-footer">
                <button class="btn btn-link text-danger mr-auto" @click="handleDelete" v-if="editingEquipment && editingEquipment.can_delete">
                    Delete
                </button>
                <button class="btn btn-secondary" @click="disableEditingEquipment">
                    Cancel
                </button>
                <button class="btn btn-primary" :disabled="buttonsDisabled" @click="handleEquipmentSave">
                    Save
                </button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import listEquipmentQuery from '@views/Setup/EquipmentManager/queryListEquipment.graphql';
import updateEquipmentMutation from '@views/Setup/EquipmentManager/mutationUpdateEquipment.graphql';
import createEquipmentMutation from '@views/Setup/EquipmentManager/mutationCreateEquipment.graphql';
import deleteEquipmentMutation from '@views/Setup/EquipmentManager/mutationDeleteEquipment.graphql';

const datePickerConfig = (config = {}) => {
  return {
    wrap: true, // set wrap to true only when using 'input-group'
    altInput: true,
    enableTime: false,
    nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
    prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
    ...config,
  };
};

/**
 * @property {String} searchTerm
 */
export default {
  name: 'EquipmentManager',
  apollo: {
    equipment: {
      query: listEquipmentQuery,
      update: data => data.filterEquipment.data,
    },
  },
  data() {
    return {
      equipment: [],
      searchEnabled: false,
      editingEnabled: false,
      editingEquipment: null,
      name: 'Spray Unit',
      equipmentImage: null,
      equipmentImageProgress: 0,
      equipmentImageProgressVariant: 'primary',
      equipmentImageId: null,
      buttonsDisabled: false,
      calibrated_on: null,
      searchTerm: '',
      loading: 0,
      datePicker: datePickerConfig({
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      }),
    };
  },
  computed: {
    table() {
      return {
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'calibrated_on', label: 'Last Calibration Date' },
          { key: 'actions', label: '', class: 'text-right' },
        ],
      };
    },
    filteredData() {
      const fullName = (item) => `${item.name}`;
      const subjectMatchesSearch = subject => {
        return (this.searchTerm.length === 0 || fullName(subject).toLowerCase().includes(this.searchTerm.toLowerCase()));
      };

      return this.equipment.filter(subjectMatchesSearch).sort((a, b) => fullName(a).localeCompare(fullName(b)));
    },
  },
  mounted() {
  },
  methods: {
    buildFileName(item) {
      if (item.image) {
        const extension = item.image.substr(item.image.lastIndexOf('.'));
        return `${item.name}${extension}`;
      }
    },
    uploadNewEquipmentImage(file) {
      this.buttonsDisabled = true;
      this.equipmentImageId = null;
      if (file) {
        this.$uploadSingleFile(file, {}, progress => {
            this.equipmentImageProgress = Math.round(progress.loaded * 100 / progress.total);
          })
          .then(({ data: { file_id } }) => {
            this.buttonsDisabled = false;
            this.equipmentImageProgressVariant = 'success';
            this.equipmentImageId = file_id;
            console.log(file_id);
          })
          .catch(err => {
            this.buttonsDisabled = false;
            this.equipmentImageProgressVariant = 'danger';
            console.error('failed', err);
            this.$notifyFailure('Unfortunately, we were unable to do that!');
          });
      }
    },
    handleEquipmentSave(evt) {
      evt.preventDefault();
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful && this.editingEquipment) {
          this.updateEquipment();
        } else if (validationWasSuccessful) {
          this.createEquipment();
        }
      });
    },

    updateEquipment() {
      const input = {
        id: this.editingEquipment.id,
        name: this.name,
        calibrated_on: this.calibrated_on,
      };

      if (this.equipmentImageId) {
        input.image_id = this.equipmentImageId;
      }

      this.$apollo.mutate({
          mutation: updateEquipmentMutation,
          variables: { input },
        })
        .then(() => {
          this.$notifySuccess('Equipment updated!');
          this.disableEditingEquipment();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    createEquipment() {
      const input = {
        name: this.name,
        calibrated_on: this.calibrated_on,
      };

      if (this.equipmentImageId) {
        input.image_id = this.equipmentImageId;
      }

      this.$apollo.mutate({
          mutation: createEquipmentMutation,
          variables: { input },
        })
        .then(() => {
          this.$notifySuccess('Equipment created!');
          this.$apollo.queries.equipment.refetch();
          this.disableEditingEquipment();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    enableCreatingEquipment() {
      this.editingEnabled = true;
    },
    enableEditingEquipment(equipment) {
      this.editingEquipment = equipment;
      this.name = equipment.name;
      this.calibrated_on = equipment.calibrated_on;
      this.editingEnabled = true;
    },
    disableEditingEquipment() {
      this.editingEquipment = null;
      this.name = 'Spray Unit';
      this.equipmentImage = null;
      this.equipmentImageId = null;
      this.equipmentImageProgress = 0;
      this.equipmentImageProgressVariant = 'primary';
      this.calibrated_on = null;
      this.editingEnabled = false;
    },
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
    handleDelete() {
      this.$apollo
        .mutate({
          mutation: deleteEquipmentMutation,
          variables: {
            input: {
              id: this.editingEquipment.id,
            },
          },
        })
        .then(() => {
          this.$notifySuccess('Equipment deleted!');
          this.$apollo.queries.equipment.refetch();
          this.disableEditingEquipment();
        })
        .catch(err => this.$mapGraphQLError(err))
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!',);
        });
    },
  },
};
</script>
